import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Download, Search, Eye, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  paid_at: string | null;
  description: string | null;
  external_id: string | null;
}

interface ClienteContasReceberTabProps {
  customerId: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

const statusColors: Record<string, string> = {
  pending: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  paid: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  overdue: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]',
  cancelled: 'bg-muted text-muted-foreground'
};

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado'
};

export function ClienteContasReceberTab({ customerId }: ClienteContasReceberTabProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['customer-invoices', customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('customer_id', customerId)
        .in('status', ['pending', 'overdue'])
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      return data as Invoice[];
    }
  });

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !search || 
      inv.description?.toLowerCase().includes(search.toLowerCase()) ||
      inv.external_id?.includes(search);
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPending = invoices.filter(i => i.status === 'pending').reduce((sum, inv) => sum + inv.amount, 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-[hsl(var(--basic-50))] rounded-lg border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">A Vencer</p>
          <p className="text-2xl font-bold text-[hsl(var(--basic-900))]">{formatCurrency(totalPending)}</p>
          <p className="text-xs text-muted-foreground">{invoices.filter(i => i.status === 'pending').length} faturas</p>
        </div>
        <div className="p-4 bg-[hsl(var(--careful-50))] rounded-lg border border-[hsl(var(--careful-200))]">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Vencido</p>
          <p className="text-2xl font-bold text-[hsl(var(--careful-600))]">{formatCurrency(totalOverdue)}</p>
          <p className="text-xs text-[hsl(var(--careful-600))]">{invoices.filter(i => i.status === 'overdue').length} faturas</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="pending">Pendentes</TabsTrigger>
            <TabsTrigger value="overdue">Vencidas</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            XLS
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_8rem_6rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
          <span>Valor</span>
          <span>Descrição</span>
          <span>Vencimento</span>
          <span>Status</span>
          <span className="text-right">Ações</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Carregando...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground italic">
            Nenhuma conta a receber encontrada.
          </div>
        ) : (
          <div className="divide-y">
            {filteredInvoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className="grid grid-cols-[1fr_1.5fr_1fr_8rem_6rem] gap-4 p-4 hover:bg-muted/30 transition-colors items-center"
              >
                <span className="font-bold text-[hsl(var(--basic-900))]">
                  {formatCurrency(invoice.amount)}
                </span>
                <span className="text-sm text-muted-foreground truncate">
                  {invoice.description || 'Sem descrição'}
                </span>
                <span className="text-sm text-[hsl(var(--basic-700))]">
                  {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase text-center ${statusColors[invoice.status]}`}>
                  {statusLabels[invoice.status]}
                </span>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                    <Link to={`/checkout/${invoice.id}`} target="_blank">
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
